import { Stack } from '@src/components/Core';
import { DrawerContent } from '@src/components/Drawer';
import type { useEntityAdmin } from '@src/components/EntityAdmin/useEntityAdmin';
import { useCallback, useMemo, useState } from 'react';
import { useEntityAdminFormState } from '../../hooks/useEntityAdminFormState';
import type { BaseEntityAdminProps, EntityAdminRecord } from '../../types';
import { createSaveContext } from '../../utils';
import { EntityAdminDrawerFooter } from '../EntityAdminDrawerFooter';
import type { BaseEntityAdminDrawerProps } from '../InputsAndDropdownsDrawer/InputsAndDropdownsDrawer';
import { CustomDrawerProvider, type CustomDrawerContextValue } from './CustomDrawerContext';

export interface CustomDrawerProps<TRecord extends EntityAdminRecord, TDrawerRecord extends EntityAdminRecord>
  extends
    BaseEntityAdminDrawerProps<TRecord, TDrawerRecord>,
    Pick<
      ReturnType<typeof useEntityAdmin<TRecord, TDrawerRecord>>,
      'selectedEntities' | 'getEntityAsDrawerEntity' | 'setIsEntityAdminDrawerDirty' | 'newEntityDefault'
    > {
  isEditing: boolean;
  addingChildEntity: boolean;
  customDrawerLayout: NonNullable<BaseEntityAdminProps<TRecord, TDrawerRecord>['customDrawerLayout']>;
}

export function CustomDrawer<TRecord extends EntityAdminRecord, TDrawerRecord extends EntityAdminRecord>({
  selectedEntities,
  onSaveEntities,
  drawerOptions,
  isEditing,
  addingChildEntity,
  getEntityAsDrawerEntity,
  setIsEntityAdminDrawerDirty,
  customDrawerLayout: CustomLayout,
  newEntityDefault,
  suppressEditingFooterActions,
}: CustomDrawerProps<TRecord, TDrawerRecord>) {
  const [visibleTabs, setVisibleTabs] = useState<string[]>([]);

  const {
    form,
    handleOnFormUpdate,
    inheritedValues,
    someError,
    prepareFormForSave,
    originalEntity,
    touchAll,
    errorState,
    touchState,
  } = useEntityAdminFormState({
    selectedEntities,
    drawerOptions,
    getEntityAsDrawerEntity,
    setIsEntityAdminDrawerDirty,
    newEntityDefault,
    visibleTabs,
    isEditing,
    addingChildEntity,
  });

  const handleOnSave = useCallback(async () => {
    const formWithInputTypesConverted = prepareFormForSave();
    // Pass EntityAdminClass[] directly (undefined if adding a new entity)
    const entityClasses = isEditing ? selectedEntities : undefined;

    const context = createSaveContext({ isEditing, addingChildEntity, selectedEntities });

    await onSaveEntities(entityClasses, formWithInputTypesConverted, context);
  }, [prepareFormForSave, onSaveEntities, isEditing, addingChildEntity, selectedEntities]);

  const contextValue = useMemo((): CustomDrawerContextValue<TDrawerRecord> => {
    return {
      form,
      isEditing,
      onFormUpdate: handleOnFormUpdate,
      inheritedValues,
      drawerOptions,
      originalEntity,
      errorState,
      touchState,
      addingChildEntity,
      setVisibleTabs,
    };
  }, [
    form,
    isEditing,
    handleOnFormUpdate,
    inheritedValues,
    drawerOptions,
    originalEntity,
    errorState,
    touchState,
    addingChildEntity,
  ]);

  return (
    <Stack data-testid="entity-admin-page-custom-drawer" h="100%">
      <DrawerContent overflow="auto" m="0" p="0" data-testid="custom-drawer-content">
        <CustomDrawerProvider value={contextValue}>
          <CustomLayout />
        </CustomDrawerProvider>
      </DrawerContent>
      {!(suppressEditingFooterActions && isEditing) && (
        <EntityAdminDrawerFooter handleOnSave={handleOnSave} someError={someError} runValidation={touchAll} />
      )}
    </Stack>
  );
}
